import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import { categoryVisualMap } from "../CreateListing/categoryVisualMap";
import { compressImage } from "../../utils/imageCompress";
import DynamicHeader from "../Header/DynamicHeader";
import { useToast } from "../Alerts/Toast";
import addImageIcon from "../CreateListing/img/add-image.png";
import "./EditSearch.scss";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const EditSearch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const toast = useToast();

  const [listing, setListing] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [photoObj, setPhotoObj] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [message, setMessage] = useState("");

  const selectRef = useRef(null);
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);

  const categories = Object.keys(categoryVisualMap);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showCategories) return;
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") setShowCategories(false);
    };

    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showCategories]);

  const scrollToAndMark = useCallback((ref, className = "field-error") => {
    const node = ref?.current ?? ref;
    if (!node) return;

    node.classList.add(className);
    node.scrollIntoView?.({ behavior: "smooth", block: "center" });

    const inputEl = node.querySelector?.("input, textarea, select") || node;
    inputEl.focus?.();

    setTimeout(() => node.classList.remove(className), 2400);
  }, []);

  useEffect(() => {
    const loadSearch = async () => {
      try {
        setLoading(true);

        const res = await axios.get(`${API_BASE}/api/listings/${id}`);
        const item = res.data;

        if (item?.type !== "wanted") {
          toast.error("Це не запит типу «Шукаю»", 3000);
          navigate(`/edit-listing/${id}`);
          return;
        }

        if (userId && String(item.userId) !== String(userId)) {
          toast.error("Ви не маєте прав редагувати цей запит", 3200);
          navigate(-1);
          return;
        }

        setListing(item);
        setTitle(item.title || "");
        setDescription(item.description || "");
        setCategory(item.category || "");
        setLocation(item.location || "");

        const photoUrl =
          item.mainPhoto ||
          (Array.isArray(item.photos) && item.photos[0]) ||
          null;

        setPhotoObj(
          photoUrl
            ? {
                url: photoUrl,
                uploading: false,
                progress: 100,
                isLocal: false,
              }
            : null
        );
      } catch (err) {
        console.error("❌ Помилка завантаження запиту", err);
        toast.error("Не вдалося завантажити запит", 3200);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    if (id) loadSearch();
  }, [id, userId, navigate, toast]);

  const triggerFileDialog = () => fileInputRef.current?.click();

  const uploadSingleFile = async (file, onProgress) => {
    const compressed = await compressImage(file, {
      maxSide: 1600,
      mimeType: "image/webp",
      quality: 0.82,
    });

    const fd = new FormData();
    fd.append("photo", compressed, compressed.name);

    const res = await axios.post(`${API_BASE}/api/listings/upload-photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        onProgress?.(pct);
      },
    });

    return res.data.photoUrl;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tmpUrl = URL.createObjectURL(file);
    setPhotoObj({ url: tmpUrl, uploading: true, progress: 0, isLocal: true });

    try {
      setUploading(true);

      const uploadedUrl = await uploadSingleFile(file, (pct) => {
        setPhotoObj((prev) => (prev ? { ...prev, progress: pct } : prev));
      });

      setPhotoObj({
        url: uploadedUrl,
        uploading: false,
        progress: 100,
        isLocal: false,
      });
    } catch (err) {
      console.error("❌ Помилка завантаження фото", err);
      toast.error("Помилка завантаження фото", 3200);
      setPhotoObj((prev) =>
        prev ? { ...prev, uploading: false, error: true } : prev
      );
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const handleRemovePhoto = () => setPhotoObj(null);

  const handleSelectCategory = (name) => {
    setCategory(name);
    setShowCategories(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!listing?.createdAt) {
      toast.error("Не знайдено createdAt запиту", 3200);
      return;
    }

    if (!title.trim()) {
      setMessage("🔎 Напишіть, що саме ви шукаєте");
      scrollToAndMark(titleRef);
      return;
    }

    if (!category) {
      setMessage("🗂️ Оберіть категорію");
      if (selectRef.current) scrollToAndMark(selectRef);
      return;
    }

    try {
      setSaving(true);

      const token = await getAccessToken();

      if (!token) {
        toast.error("Сесія недійсна. Увійдіть повторно.", 3200);
        return;
      }

      const payload = {
        createdAt: listing.createdAt,
        title: title.trim(),
        description: description.trim(),
        category,
        location: location.trim(),
        photos: photoObj?.url ? [photoObj.url] : [],
        type: "wanted",
        };

      await axios.patch(`${API_BASE}/api/listings/${id}/wanted`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Запит оновлено", 2200);
      navigate(-1);
    } catch (err) {
      console.error("❌ Помилка оновлення запиту", err?.response?.data || err);
      toast.error(
        err?.response?.data?.error || "Не вдалося оновити запит",
        3200
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <DynamicHeader
          title="Редагувати запит"
          variant="glass"
          size="compact"
          hideOnScroll={true}
          showBack={true}
          showMenu={false}
        />
        <div className="local-header-spacer small" />
        <div className="edit-search">
          <p className="edit-search-loading">Завантаження...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicHeader
        title="Редагувати запит"
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        showMenu={false}
      />
      <div className="local-header-spacer small" />

      <div
        className={`edit-search ios-glass-card ${
          showCategories ? "dropdown-open" : ""
        }`}
      >
        <h2>Редагувати запит</h2>
        {message && <p className="message">{message}</p>}

        <form onSubmit={handleSubmit} className="edit-search-form" noValidate>
          <input
            ref={titleRef}
            type="text"
            className="pill-50"
            placeholder="Що ви шукаєте? *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <textarea
            className="pill-50"
            placeholder="Опишіть детальніше, що саме вам потрібно"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="custom-select-wrapper" ref={selectRef}>
            <div
              className={`custom-select-trigger pill-50 ${
                showCategories ? "open" : ""
              }`}
              onClick={() => setShowCategories((s) => !s)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") &&
                setShowCategories((s) => !s)
              }
            >
              {category ? (
                <div className="selected-category">
                  <div
                    className="category-icon"
                    style={{
                      backgroundImage: `url(${categoryVisualMap[category]})`,
                    }}
                  />
                  <span>{category}</span>
                </div>
              ) : (
                <span className="placeholder">Категорія *</span>
              )}

              <span className="chevron" aria-hidden="true">
                ▾
              </span>
            </div>

            <div
              className={`custom-select-dropdown ${
                showCategories ? "open" : "closed"
              }`}
              role="listbox"
              aria-hidden={!showCategories}
            >
              {categories.map((name, idx) => (
                <div
                  key={name}
                  className="dropdown-item"
                  style={{ "--item-index": idx }}
                  onClick={() => handleSelectCategory(name)}
                  role="option"
                  aria-selected={category === name}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleSelectCategory(name);
                    }
                  }}
                >
                  <div
                    className="category-icon"
                    style={{
                      backgroundImage: `url(${categoryVisualMap[name]})`,
                    }}
                  />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>

          <input
            type="text"
            className="pill-50"
            placeholder="Локація (необов'язково)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />

          <div className="photo-upload">
            <label>Фото (необов'язково)</label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: "none" }}
              disabled={uploading}
            />

            {!photoObj && (
              <button
                type="button"
                className="photo-tile empty add-one"
                onClick={triggerFileDialog}
                disabled={uploading}
              >
                <img
                  src={addImageIcon}
                  alt="Додати фото"
                  className="add-image-icon"
                />
              </button>
            )}
          </div>

          {photoObj && (
            <div className="photo-grid single">
              <div
                className={`tile filled ${
                  photoObj.uploading ? "is-uploading" : ""
                }`}
              >
                <img src={photoObj.url} alt="Фото запиту" />

                {!photoObj.uploading && <span className="main-badge">Фото</span>}

                {photoObj.uploading && (
                  <div className="progress-overlay">
                    <div className="ring">
                      <svg viewBox="0 0 36 36" width="64" height="64">
                        <path
                          className="bg"
                          d="M18 2.0845
                             a 15.9155 15.9155 0 0 1 0 31.831
                             a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                        <path
                          className="meter"
                          strokeDasharray={`${Math.max(
                            1,
                            Math.min(100, photoObj.progress ?? 0)
                          )}, 100`}
                          d="M18 2.0845
                             a 15.9155 15.9155 0 0 1 0 31.831
                             a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <div className="pct">{photoObj.progress ?? 0}%</div>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="remove-btn"
                  onClick={handleRemovePhoto}
                  disabled={photoObj.uploading}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="cta-sticky">
            <button
              type="submit"
              disabled={saving || uploading}
              className="primary-cta with-sheen"
            >
              {saving ? "Збереження..." : uploading ? "Завантаження..." : "Зберегти зміни"}
            </button>
          </div>

          <div className="bottom-safe-space" />
        </form>
      </div>
    </>
  );
};

export default EditSearch;